import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, NetworkError } from '../lib/apiClient.js';
import { newId } from '../lib/id.js';
import { deviceTimeZone, setGameTimeZone } from '../lib/gameTime.js';
import { getCachedPlayerState, setCachedPlayerState, db, type PendingMessage } from '../lib/db.js';
import { uploadImage } from '../lib/uploadImage.js';
import { useOnlineStatus } from '../lib/onlineStatus.js';
import { useGeolocation, type GeoPermission } from '../lib/geolocation.js';
import type {
  OpenMissionRequest,
  OpenMissionResult,
  PlayerStateDTO,
  SendMessageRequest,
  SendMessageResult,
} from '@shared/api.js';
import { evaluateMission, stubMissionFromPreview } from '@shared/index.js';
import { DEFAULT_SETTINGS } from '@shared/types.js';

function newEventId(): string {
  return newId();
}

export interface GameEngine {
  state: PlayerStateDTO | null;
  loading: boolean;
  online: boolean;
  serverReachable: boolean;
  refresh: () => Promise<void>;
  geoPermission: GeoPermission;
  geoError: string | null;
  requestLocationPermission: () => void;
  /** המיקום האחרון של המכשיר (null = עדיין אין) — לתצוגה במפה ולמצב ה-GPS */
  geoPosition: GeolocationPosition | null;
  /** בדיקת מיקום מיידית לפי בקשת המשתמש */
  refreshLocation: () => void;
  needsLocation: boolean;
  distanceMeters: number | null;
  awaitingSignal: boolean;
  lastOpenError: string | null;
  sendMessage: (
    text: string,
    imageBlob?: Blob | null,
  ) => Promise<{ ok: true; queued: boolean } | { ok: false; error: string }>;
  pendingMessageCount: number;
}

const MIN_POLL_MS = 3_000;

export function useGameEngine(): GameEngine {
  const online = useOnlineStatus();
  const [state, setState] = useState<PlayerStateDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [awaitingSignal, setAwaitingSignal] = useState(false);
  const [lastOpenError, setLastOpenError] = useState<string | null>(null);
  const [distanceMeters, setDistanceMeters] = useState<number | null>(null);

  const stateRef = useRef<PlayerStateDTO | null>(null);
  stateRef.current = state;
  const inFlightId = useRef<string | null>(null);
  const eventIdForMission = useRef<Map<string, string>>(new Map());

  // כל הצגת שעה באפליקציה משתמשת באזור הזמן של המבצע — מעדכנים לפני שהצאצאים מתרנדרים
  setGameTimeZone(state?.settings.timeZone);

  const needsLocation = Boolean(state?.nextPending && state.nextPending.hasLocationCondition);
  const geo = useGeolocation(needsLocation);

  const applyState = useCallback((next: PlayerStateDTO) => {
    setState(next);
    void setCachedPlayerState(next);
  }, []);

  const load = useCallback(async () => {
    try {
      // אזור הזמן של המכשיר נשלח כדי שהמנהל יקבל אזהרה אם המבצע עבר לחו"ל והגדרת אזור הזמן לא עודכנה
      const fresh = await api.get<PlayerStateDTO>(`/api/player/state?tz=${encodeURIComponent(deviceTimeZone())}`);
      applyState(fresh);
      setLoading(false);
      return fresh;
    } catch {
      // Offline או שרת לא זמין — נופלים חזרה על המטמון האחרון (Offline First)
      const cached = await getCachedPlayerState();
      if (cached) setState(cached);
      setLoading(false);
      return null;
    }
  }, [applyState]);

  // טעינה ראשונית מיידית מהמטמון, ואז ניסיון רענון מהשרת
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cached = await getCachedPlayerState();
      if (cached && !cancelled) {
        setState(cached);
        setLoading(false);
      }
      await load();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling תקופתי לפי תדירות הסנכרון שהוגדרה על ידי המנהל (עם רצפה שמונעת עומס יתר;
  // 0 = "ידני בלבד", ואז אין polling אוטומטי כלל — ראו SYNC_INTERVAL_OPTIONS)
  const configuredIntervalMs = state?.settings.syncIntervalMs ?? DEFAULT_SETTINGS.syncIntervalMs;
  // פתרון שממתין לאישור המפקדה — בודקים בתדירות הגבוהה ביותר, כדי שהמשימה הבאה תופיע ברגע שהתאשרה
  const awaitingReview = state?.solution?.submissions[0]?.status === 'pending';
  useEffect(() => {
    if (!online || configuredIntervalMs <= 0) return;
    const interval = awaitingReview ? MIN_POLL_MS : Math.max(configuredIntervalMs, MIN_POLL_MS);
    const id = window.setInterval(() => {
      void load();
    }, interval);
    return () => window.clearInterval(id);
  }, [online, load, configuredIntervalMs, awaitingReview]);

  // רענון מיידי כשחוזר חיבור
  useEffect(() => {
    if (online) void load();
  }, [online, load]);

  // רענון מיידי כשחוזרים לאפליקציה (מעבר בין לשוניות, נעילת/פתיחת מסך, חזרה מהרקע) — טיימרים
  // בדפדפן נעצרים ברקע, ובלי זה השחקן היה רואה מצב ישן עד לסקר הבא. מוגבל בקצב כדי לא להציף.
  useEffect(() => {
    let last = 0;
    const refreshNow = () => {
      if (document.visibilityState === 'hidden') return;
      const t = Date.now();
      if (t - last < 1_500) return;
      last = t;
      void load();
    };
    document.addEventListener('visibilitychange', refreshNow);
    window.addEventListener('focus', refreshNow);
    window.addEventListener('pageshow', refreshNow);
    return () => {
      document.removeEventListener('visibilitychange', refreshNow);
      window.removeEventListener('focus', refreshNow);
      window.removeEventListener('pageshow', refreshNow);
    };
  }, [load]);

  const attemptOpen = useCallback(
    async (req: OpenMissionRequest) => {
      if (inFlightId.current === req.clientEventId) return;
      inFlightId.current = req.clientEventId;
      try {
        const result = await api.post<OpenMissionResult>('/api/player/missions/open', req);
        if (result.ok) {
          setLastOpenError(null);
          setAwaitingSignal(false);
          await load();
        } else if (result.code === 'not_pending') {
          // כנראה כבר נפתחה בערוץ אחר — סנכרון יעדכן את המצב
          await load();
        } else {
          setLastOpenError('reason' in result ? result.reason : 'המשימה לא נמצאה');
        }
      } catch (e) {
        if (e instanceof NetworkError) {
          setAwaitingSignal(true);
        } else if (e instanceof ApiError) {
          setLastOpenError(e.message);
        }
      } finally {
        inFlightId.current = null;
      }
    },
    [load],
  );

  // ===== הודעות שחקן: Offline-first — נשמר מקומית תמיד, נשלח מיד אם יש רשת,
  // ומתרוקן אוטומטית ברגע שהחיבור חוזר (סדר שליחה נשמר) =====
  const [pendingMessageCount, setPendingMessageCount] = useState(0);

  const refreshPendingMessageCount = useCallback(async () => {
    setPendingMessageCount(await db.pendingMessages.count());
  }, []);

  useEffect(() => {
    void refreshPendingMessageCount();
  }, [refreshPendingMessageCount]);

  /**
   * מעבד פתק אחד: אם יש תמונה ממתינה בלי URL עדיין — מעלה אותה קודם (שלב 1),
   * ורק אז שולח את הפתק עצמו (שלב 2). כך תמונה גדולה שנתקעת באמצע ההעלאה לא
   * "מאבדת" את הפתק — הבלוב נשאר מקומית עד שההעלאה מצליחה בפועל.
   */
  const deliverPendingMessage = useCallback(async (p: PendingMessage): Promise<void> => {
    let imageUrl = p.imageUrl;
    if (p.imageBlob && !imageUrl) {
      imageUrl = await uploadImage(p.imageBlob);
      await db.pendingMessages.update(p.id, { imageUrl, imageBlob: null });
    }
    const req: SendMessageRequest = {
      id: p.id,
      text: p.text,
      imageUrl,
      missionId: p.missionId,
      isTest: p.isTest,
    };
    const result = await api.post<SendMessageResult>('/api/player/messages', req);
    if (!result.ok) throw new ApiError(result.reason, 400, result);
    await db.pendingMessages.delete(p.id);
  }, []);

  const flushingMessages = useRef(false);
  const flushPendingMessages = useCallback(async () => {
    if (flushingMessages.current) return;
    flushingMessages.current = true;
    try {
      const pending = await db.pendingMessages.orderBy('createdAt').toArray();
      for (const p of pending) {
        try {
          await deliverPendingMessage(p);
        } catch (e) {
          if (e instanceof NetworkError) break; // עדיין בלי רשת — ננסה שוב בפעם הבאה, סדר נשמר
          await db.pendingMessages.delete(p.id); // קלט לא תקין בשרת — לא ננסה שוב לנצח
        }
      }
    } finally {
      flushingMessages.current = false;
      await refreshPendingMessageCount();
    }
  }, [deliverPendingMessage, refreshPendingMessageCount]);

  useEffect(() => {
    if (online) void flushPendingMessages().then(() => load());
  }, [online, flushPendingMessages, load]);

  const sendMessage = useCallback(
    async (
      text: string,
      imageBlob?: Blob | null,
    ): Promise<{ ok: true; queued: boolean } | { ok: false; error: string }> => {
      const trimmed = text.trim();
      if (!trimmed && !imageBlob) return { ok: false, error: 'הפתק ריק' };
      const pending: PendingMessage = {
        id: newId(),
        text: trimmed,
        imageBlob: imageBlob ?? null,
        imageUrl: null,
        missionId: stateRef.current?.current?.missionId ?? null,
        isTest: stateRef.current?.settings.testMode.enabled ?? false,
        createdAt: new Date().toISOString(),
        attempts: 0,
        lastError: null,
      };
      await db.pendingMessages.put(pending);
      await refreshPendingMessageCount();
      if (!online) return { ok: true, queued: true };
      try {
        await deliverPendingMessage(pending);
        await refreshPendingMessageCount();
        await load();
        return { ok: true, queued: false };
      } catch (e) {
        if (e instanceof ApiError) {
          // קלט לא תקין (למשל שרת דחה) — לא משאירים בתור לנצח
          await db.pendingMessages.delete(pending.id);
          await refreshPendingMessageCount();
          return { ok: false, error: e.message };
        }
        // NetworkError — נשאר בתור המקומי, יישלח אוטומטית כשהחיבור יחזור
        return { ok: true, queued: true };
      }
    },
    [online, load, refreshPendingMessageCount, deliverPendingMessage],
  );

  // מנוע הערכת פתיחה מקומית: רץ באופן קבוע, גם Offline, ומפעיל פתיחה כשתנאי מתקיים
  useEffect(() => {
    const tick = () => {
      const s = stateRef.current;
      // שורה ריקה בגיליון = מקום שמור למשימה עתידית; לא פותחים אותה גם כשתנאי הפתיחה שלה מתקיימים
      if (!s?.nextPending || !s.nextPending.hasContent) return;
      const testMode = s.settings.testMode;
      const now = testMode.enabled && testMode.fakeNowIso ? new Date(testMode.fakeNowIso) : new Date();
      const useFakePos = testMode.enabled && testMode.fakeLat !== null && testMode.fakeLng !== null;
      const position = useFakePos
        ? { lat: testMode.fakeLat!, lng: testMode.fakeLng! }
        : geo.position
          ? { lat: geo.position.coords.latitude, lng: geo.position.coords.longitude }
          : null;

      const mission = stubMissionFromPreview(s.nextPending);
      const evalResult = evaluateMission(mission, {
        now,
        position,
        radiusMeters: s.settings.gpsRadiusMeters,
        openedMissionIds: new Set(),
        timeZone: s.settings.timeZone,
      });

      if (evalResult.distanceMeters !== null) setDistanceMeters(evalResult.distanceMeters);

      if (evalResult.state !== 'available') return;
      // כשפתיחה אוטומטית לפי זמן כבויה בהגדרות, הזמן לבדו לא פותח משימה (אפשור ידני/מיקום כן)
      const timeMayOpen = s.settings.autoOpenByTime && !s.nextPending.autoOpenHeld;
      // המשימה הקודמת דורשת פתרון שעוד לא אושר — זמן/מיקום לא פותחים (רק אפשור ידני של המנהל)
      const gated = s.nextPending.gatedBySolution;
      const satisfied = evalResult.satisfied.filter((m) => (gated ? m === 'manual' : m !== 'time' || timeMayOpen));
      if (satisfied.length === 0) return;

      const missionId = s.nextPending.id;
      let clientEventId = eventIdForMission.current.get(missionId);
      if (!clientEventId) {
        clientEventId = newEventId();
        eventIdForMission.current.set(missionId, clientEventId);
      }
      const method = satisfied[0]!;
      void attemptOpen({
        missionId,
        method,
        latitude: position?.lat ?? null,
        longitude: position?.lng ?? null,
        accuracy: geo.position?.coords.accuracy ?? null,
        clientEventId,
        isTest: testMode.enabled,
      });
    };
    tick();
    const id = window.setInterval(tick, 2_000);
    return () => window.clearInterval(id);
  }, [geo.position, attemptOpen]);

  return {
    state,
    loading,
    online,
    serverReachable: online,
    refresh: async () => {
      await load();
    },
    geoPermission: geo.permission,
    geoError: geo.error,
    requestLocationPermission: geo.requestPermission,
    geoPosition: geo.position,
    refreshLocation: geo.refresh,
    needsLocation,
    distanceMeters,
    awaitingSignal,
    lastOpenError,
    sendMessage,
    pendingMessageCount,
  };
}
